import {CommonModule} from '@angular/common';
import {ChangeDetectionStrategy, Component} from '@angular/core';
import {FormsModule, ReactiveFormsModule, UntypedFormBuilder, UntypedFormControl, Validators} from '@angular/forms';
import {ActivatedRoute} from '@angular/router';
import {NbButtonModule, NbCheckboxModule, NbDialogService, NbFormFieldModule, NbInputModule, NbSpinnerModule,} from '@nebular/theme';
import {UntilDestroy} from '@ngneat/until-destroy';
import {NgxMaskDirective} from 'ngx-mask';
import {filter, map, startWith, take, withLatestFrom} from 'rxjs';
import {CountControlComponent, CountControlValueInterface,} from 'src/app/booking/booking-confirmation/count-control/count-control.component';
import {TermsConditionsDialogComponent,} from 'src/app/dialog/terms-conditions-dialog/terms-conditions-dialog.component';
import {AvailableVehicleInterface, EquipmentInterface, VehicleChargeInterface, PricedCoverage} from 'src/app/core/interfaces/rates.interfaces';
import {ReservationDTO} from 'src/app/core/interfaces/reservations.interfaces';
import {searchFormToDTO} from 'src/app/core/mappers/rates.mappers';
import {onlyLettersValidator} from 'src/app/core/validators';
import {CarCalculationsService} from 'src/app/core/services/car-calculations.service';
import {ImgPlaceholderDirective} from '../../core/directives/img-placeholder.directive';
import {RatesDataService} from '../../core/services/rates-data.service';
import {ReservationDataService} from '../../core/services/reservation-data.service';
import {SearchResultsDataService} from '../../core/services/search-results-data.service';
import {ErrorMessageComponent} from '../../core/components/error-message/error-message.component';
import {IconListComponent} from '../booking-search-list/icon-list/icon-list.component';
import {FleetInterface} from "../../core/interfaces/fleet.interface";
import {getFleetTerms, getFleetLogoUrlByIdAndRateGroup} from "../../core/helpers";
import {FleetsDataService} from "../../core/services/fleets-data.service";
import {RentalPeriodService} from "../../core/services/rental-period.service";

export interface AppliedEquipmentInterface {
  quantity: number;
  price: number;
  ota_code: number;
  name: string;
  itemType: 'equipment' | 'vehicle_charge' | 'coverage';
}

type AdditionalItemType = EquipmentInterface | VehicleChargeInterface | PricedCoverage;

@UntilDestroy()
@Component({
  selector: 'car-booking-confirmation',
  templateUrl: './booking-confirmation.component.html',
  styleUrls: ['./booking-confirmation.component.scss'],
  providers: [CarCalculationsService],
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    CommonModule,
    NbCheckboxModule,
    NbButtonModule,
    NbSpinnerModule,
    ErrorMessageComponent,
    NbFormFieldModule,
    NgxMaskDirective,
    ReactiveFormsModule,
    FormsModule,
    CountControlComponent,
    IconListComponent,
    ImgPlaceholderDirective,
    NbInputModule,
  ],
})
export class BookingConfirmationComponent {
  agreedWithTerms = false;
  agreedWithFleetTerms = false;

  additionalOptionsForm = this.fb.group({});
  personalForm = this.fb.group({
    first_name: [null, [Validators.required, Validators.maxLength(20), onlyLettersValidator()]],
    last_name: [null, [Validators.required, Validators.maxLength(20), onlyLettersValidator()]],
    email: [null, [Validators.required, Validators.email]],
    phone_number: [null, Validators.required],
    flight_number: [null],
  });

  car$ = this.searchResultDataService.selectedCar$.pipe(filter(Boolean));
  reservationPending$ = this.reservationsDataService.isLoading$;
  bookingDays$ = this.ratesDataService.bookingDays$;
  currency$ = this.ratesDataService.currency$;
  equipmentTotal$ = this.additionalOptionsForm.valueChanges.pipe(
    startWith(null),
    map(() => {
      // Sum up all prices from applied equipment
      // Note: price already includes rental period multiplier from CountControlComponent
      return this.appliedEquipment.reduce((prev: number, curr: AppliedEquipmentInterface) => prev + curr.price, 0);
    })
  );

  fleetTermsUrl$ = this.fleetsDataService.fleets$.pipe(
    map((fleets: FleetInterface[]) => {
      return getFleetTerms(this.carCalculationsService.selectedCar.vehicle.fleet_id, fleets);
    }),
  );

  fleetLogoUrl$ = this.car$.pipe(
    withLatestFrom(this.fleetsDataService.fleets$),
    map(([car, fleets]: [AvailableVehicleInterface, FleetInterface[]]) => {
      const rateGroup = car.rental_rate.vehicle_charges.find((e) => e.ota_code === 1)?.description ?? '';
      return getFleetLogoUrlByIdAndRateGroup(car.vehicle.fleet_id, rateGroup, fleets);
    })
  );

  allAdditionalItems$ = this.car$.pipe(
    map((car: AvailableVehicleInterface) => [
      ...car.equipment,
      ...car.rental_rate.vehicle_charges.filter(charge => charge.ota_code !== 1),
      ...car.coverages
    ])
  );



constructor(
    private fb: UntypedFormBuilder,
    private route: ActivatedRoute,
    private dialogService: NbDialogService,
    private ratesDataService: RatesDataService,
    private reservationsDataService: ReservationDataService,
    public carCalculationsService: CarCalculationsService,
    public searchResultDataService: SearchResultsDataService,
    private fleetsDataService: FleetsDataService,
    private rentalPeriodService: RentalPeriodService,
  ) {
    this.fleetsDataService.loadFleets();
    this.initAdditionalOptionsForm();
    this.car$
      .pipe(take(1))
      .subscribe((car: AvailableVehicleInterface) =>  this.carCalculationsService.selectedCar = car);
  }

  get disableButton(): boolean {
    return !(this.personalForm.valid && this.agreedWithTerms); //  && this.agreedWithFleetTerms
  }

  get bookingBlockedReason(): string | null {
    if (this.personalForm.invalid) {
      return 'Fill in your personal info to book.';
    }
    if (!this.agreedWithTerms) {
      return 'Agree with CarBro terms to book.';
    }
    return null;
  }

  get appliedEquipment(): AppliedEquipmentInterface[] {
    const optionsForm: { [key: string]: CountControlValueInterface } = this.additionalOptionsForm.getRawValue();
    const applied: AppliedEquipmentInterface[] = [];

    Object.entries(optionsForm).forEach((item: [string, CountControlValueInterface]) => {
      const parsedItem = JSON.parse(item[0]) as AdditionalItemType;
      const name = this.getItemDescription(parsedItem);
      const otaCode = this.getItemOtaCode(parsedItem);
      const itemType = this.getItemType(parsedItem);

      applied.push({
        name,
        ota_code: otaCode,
        itemType,
        ...item[1],
      });
    });

    return applied.filter((item) => item.quantity > 0);
  }

  get appliedEquipmentByType() {
    const items = this.appliedEquipment;
    return {
      equipment: items.filter(item => item.itemType === 'equipment').map(item => ({
        ota_code: item.ota_code,
        quantity: item.quantity,
      })),
      vehicle_charges: items.filter(item => item.itemType === 'vehicle_charge').map(item => ({
        ota_code: item.ota_code,
        quantity: item.quantity,
      })),
      coverages: items.filter(item => item.itemType === 'coverage').map(item => ({
        ota_code: item.ota_code,
        quantity: item.quantity,
      })),
    };
  }

  countControlName(item: AdditionalItemType): string {
    return JSON.stringify(item);
  }

  getItemDescription(item: AdditionalItemType): string {
    if ('description' in item) {
      return item.description;
    }
    if ('coverage' in item) {
      return item.coverage.coverage_text_type || 'Coverage';
    }
    return '';
  }

  private getItemOtaCode(item: AdditionalItemType): number {
    if ('ota_code' in item) {
      return item.ota_code;
    }
    if ('coverage' in item) {
      return item.coverage.coverage_type;
    }
    return 0;
  }

  private getItemType(item: AdditionalItemType): 'equipment' | 'vehicle_charge' | 'coverage' {
    // PricedCoverage has 'coverage' property
    if ('coverage' in item) {
      return 'coverage';
    }
    // VehicleChargeInterface has 'amount' property directly (not nested in 'charge')
    if ('amount' in item && !('charge' in item)) {
      return 'vehicle_charge';
    }
    // EquipmentInterface has 'charge' property
    if ('charge' in item) {
      return 'equipment';
    }
    // Default to equipment
    return 'equipment';
  }

  getItemPrice(item: AdditionalItemType): number {
    if ('charge' in item) {
      return +item.charge.amount + +(item.charge.tax_amount?.total ?? 0);
    }
    if ('amount' in item) {
      return +item.amount + +(item.tax_amount?.total ?? 0);
    }
    return 0;
  }

  private getItemRentalPeriod(item: AdditionalItemType): string | undefined {
    return item.rental_period;
  }

  getItemTotalPrice(item: AdditionalItemType, rentalDays: number): number {
    const basePrice = this.getItemPrice(item);
    const rentalPeriod = this.getItemRentalPeriod(item);
    const multiplier = this.rentalPeriodService.getMultiplier(rentalPeriod, rentalDays);
    return basePrice * multiplier;
  }

  private isItemRequired(item: AdditionalItemType): boolean {
    // Check if item is required based on different field names and types
    if ('required_ind' in item) {
      // Handle both boolean and string types
      return item.required_ind === true || item.required_ind === 'true' || item.required_ind === '1';
    }
    if ('required' in item) {
      // Handle both boolean and string types
      return item.required === true || item.required === 'true' || item.required === '1';
    }
    return false;
  }

  openTermsConditions(event?: Event): void {
    // The link sits inside the checkbox label; keep the click from toggling the box.
    event?.preventDefault();
    event?.stopPropagation();
    this.dialogService.open(TermsConditionsDialogComponent);
  }

  makeReservation() {
    this.searchResultDataService.searchForm$
      .pipe(
        filter(Boolean),
        withLatestFrom(this.car$),
        take(1),
      )
      .subscribe(([searchForm, car]: [any, AvailableVehicleInterface]) => {
        const personalForm = this.personalForm.getRawValue();
        const itemsByType = this.appliedEquipmentByType;

        // Extract rate_group from vehicle charges where ota_code === 1
        const rateGroupCharge = car.rental_rate.vehicle_charges.find(charge => charge.ota_code === 1);
        const rateGroup = rateGroupCharge?.description;

        const reservation = {
          ...searchFormToDTO(searchForm),
          primary_driver: {
            first_name: personalForm.first_name,
            last_name: personalForm.last_name,
            phone_number: personalForm.phone_number,
            email: personalForm.email,
            country_code: 'GB',
          },
          equipment: itemsByType.equipment,
          vehicle_charges: itemsByType.vehicle_charges,
          coverages: itemsByType.coverages,
          flight_number: personalForm.flight_number,
          quotation_id: car.quotation_id,
          rate_group: rateGroup,
        } as unknown as ReservationDTO;
        this.reservationsDataService.makeReservation(reservation);
      });
  }

  private initAdditionalOptionsForm(): void {
    this.car$
      .pipe(
        withLatestFrom(this.bookingDays$),
        take(1)
      )
      .subscribe(([car, rentalDays]: [AvailableVehicleInterface, number]) => {
        // Add equipment items
        car.equipment.forEach((item: EquipmentInterface) => {
          const isRequired = this.isItemRequired(item);
          const totalPrice = this.getItemTotalPrice(item, rentalDays);
          const control = new UntypedFormControl({
            price: isRequired ? totalPrice : 0,
            quantity: isRequired ? 1 : 0,
          });
          if (isRequired) {
            control.disable();
          }
          this.additionalOptionsForm.addControl(this.countControlName(item), control);
        });

        // Add vehicle charges (exclude ota_code === 1)
        car.rental_rate.vehicle_charges
          .filter(charge => charge.ota_code !== 1)
          .forEach((item: VehicleChargeInterface) => {
            const isRequired = this.isItemRequired(item);
            const totalPrice = this.getItemTotalPrice(item, rentalDays);
            const control = new UntypedFormControl({
              price: isRequired ? totalPrice : 0,
              quantity: isRequired ? 1 : 0,
            });
            if (isRequired) {
              control.disable();
            }
            this.additionalOptionsForm.addControl(this.countControlName(item), control);
          });

        // Add coverages
        car.coverages.forEach((item: PricedCoverage) => {
          const isRequired = this.isItemRequired(item);
          const totalPrice = this.getItemTotalPrice(item, rentalDays);
          const control = new UntypedFormControl({
            price: isRequired ? totalPrice : 0,
            quantity: isRequired ? 1 : 0,
          });
          if (isRequired) {
            control.disable();
          }
          this.additionalOptionsForm.addControl(this.countControlName(item), control);
        });

        // Trigger initial calculation for equipmentTotal$ to include required items
        // Use setTimeout to ensure the form is fully initialized
        setTimeout(() => {
          this.additionalOptionsForm.updateValueAndValidity();
          // Manually trigger valueChanges by patching with empty object
          this.additionalOptionsForm.patchValue({}, { emitEvent: true });
        });
      });
  }
}
